import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Upload,
  UserPlus,
  PenTool,
  Send,
  CheckCircle2,
  ArrowRight,
  ArrowLeft,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

const pageVariants = {
  initial: { opacity: 0, x: 50 },
  animate: { opacity: 1, x: 0 },
  exit: { opacity: 0, x: -50 },
};

const fadeInUp = {
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0 },
  exit: { opacity: 0, y: -20 },
};

export const WorkflowStep = ({ onNext, onBack }) => {
  const { t } = useTranslation();
  const [activeStep, setActiveStep] = useState(0);

  const workflowSteps = [
    {
      icon: Upload,
      title: t("onboarding.workflow.upload.title", "Upload Your Document"),
      description: t(
        "onboarding.workflow.upload.description",
        "Drag and drop your PDF, Word, or other document. We support all major formats.",
      ),
    },
    {
      icon: UserPlus,
      title: t("onboarding.workflow.recipients.title", "Add Recipients"),
      description: t(
        "onboarding.workflow.recipients.description",
        "Enter email addresses and assign signing order. Add as many signers as you need.",
      ),
    },
    {
      icon: PenTool,
      title: t("onboarding.workflow.fields.title", "Place Signature Fields"),
      description: t(
        "onboarding.workflow.fields.description",
        "Drag signature, date, and text fields onto your document. Our AI can auto-detect fields too!",
      ),
    },
    {
      icon: Send,
      title: t("onboarding.workflow.send.title", "Send for Signature"),
      description: t(
        "onboarding.workflow.send.description",
        "Hit send and recipients get an email with a secure link. Track progress in real-time.",
      ),
    },
    {
      icon: CheckCircle2,
      title: t("onboarding.workflow.done.title", "Done!"),
      description: t(
        "onboarding.workflow.done.description",
        "Once everyone signs, you'll get the completed document with a full audit trail.",
      ),
    },
  ];

  useEffect(() => {
    const interval = setInterval(() => {
      setActiveStep((prev) => (prev + 1) % workflowSteps.length);
    }, 3000);
    return () => clearInterval(interval);
  }, [workflowSteps.length]);

  const renderWorkflowStepClass = (index) => {
    if (index === activeStep) {
      return "bg-primary text-primary-foreground";
    }
    return index < activeStep
      ? "bg-primary/20 text-primary"
      : "bg-muted text-muted-foreground";
  };

  return (
    <motion.div
      variants={pageVariants}
      initial="initial"
      animate="animate"
      exit="exit"
      className="max-w-4xl mx-auto px-4"
    >
      <div className="text-center mb-12">
        <motion.h2
          variants={fadeInUp}
          className="text-3xl sm:text-4xl font-bold text-foreground mb-4"
        >
          {t("onboarding.workflow.title", "How eScribe Works")}
        </motion.h2>
        <motion.p variants={fadeInUp} className="text-lg text-muted-foreground">
          {t(
            "onboarding.workflow.subtitle",
            "Get documents signed in 5 simple steps",
          )}
        </motion.p>
      </div>

      <div className="relative mb-12">
        {/* Progress line */}
        <div className="absolute top-8 left-0 right-0 h-0.5 bg-muted hidden md:block">
          <motion.div
            className="h-full bg-primary"
            initial={{ width: "0%" }}
            animate={{
              width: `${(activeStep / (workflowSteps.length - 1)) * 100}%`,
            }}
            transition={{ duration: 0.5 }}
          />
        </div>

        {/* Steps */}
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4 md:gap-2">
          {workflowSteps.map((step, index) => (
            <motion.div
              key={step.title}
              variants={fadeInUp}
              className={cn(
                "flex flex-col items-center text-center cursor-pointer",
                index <= activeStep ? "opacity-100" : "opacity-40",
              )}
              onClick={() => setActiveStep(index)}
            >
              <motion.div
                className={cn(
                  "w-16 h-16 rounded-full flex items-center justify-center mb-3 relative z-10 transition-colors",
                  renderWorkflowStepClass(index),
                )}
                animate={index === activeStep ? { scale: [1, 1.1, 1] } : {}}
                transition={{ duration: 0.3 }}
              >
                <step.icon className="w-7 h-7" />
              </motion.div>
              <h3 className="font-medium text-sm mb-1">{step.title}</h3>
              <AnimatePresence mode="wait">
                {index === activeStep && (
                  <motion.p
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: "auto" }}
                    exit={{ opacity: 0, height: 0 }}
                    className="text-xs text-muted-foreground max-w-50"
                  >
                    {step.description}
                  </motion.p>
                )}
              </AnimatePresence>
            </motion.div>
          ))}
        </div>
      </div>

      {/* Animated preview */}
      <motion.div
        variants={fadeInUp}
        className="bg-muted/50 rounded-xl p-8 mb-12 flex items-center justify-center min-h-50"
      >
        <AnimatePresence mode="wait">
          <motion.div
            key={activeStep}
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.9 }}
            className="flex flex-col items-center gap-4"
          >
            <div className="w-20 h-20 rounded-full bg-primary/10 flex items-center justify-center">
              {(() => {
                const Icon = workflowSteps[activeStep].icon;
                return <Icon className="w-10 h-10 text-primary" />;
              })()}
            </div>
            <p className="text-lg font-medium text-center">
              {workflowSteps[activeStep].description}
            </p>
          </motion.div>
        </AnimatePresence>
      </motion.div>

      <div className="flex justify-between">
        <Button variant="ghost" onClick={onBack} className="gap-2">
          <ArrowLeft className="w-4 h-4" />
          {t("common.back", "Back")}
        </Button>
        <Button onClick={onNext} className="gap-2">
          {t("common.continue", "Continue")}
          <ArrowRight className="w-4 h-4" />
        </Button>
      </div>
    </motion.div>
  );
};

export default WorkflowStep;
